import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Database, Plus, ArrowClockwise, Trash, ArrowRight, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmt } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

export default function DataSources() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/datasources");
      setSources(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/datasources", { name, url });
      toast.success(`Terhubung: ${data.name} (${fmt(data.row_count)} baris)`);
      setOpen(false); setName(""); setUrl("");
      window.dispatchEvent(new Event("datasources:changed"));
      navigate(`/dashboard/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const refresh = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/datasources/${id}/refresh`);
      toast.success(`Diperbarui: ${fmt(data.row_count)} baris`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/datasources/${id}`);
      toast.success("Sumber data dihapus");
      window.dispatchEvent(new Event("datasources:changed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">Manajemen Data</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Sumber Data</h1>
          <p className="text-muted-foreground mt-3 max-w-lg text-sm">
            Sambungkan Google Sheet publik (Published to web / dibagikan) untuk dianalisa seperti Power BI.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl font-semibold gap-2" data-testid="open-connect-dialog">
              <Plus size={18} weight="bold" /> Hubungkan Sheet
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border" data-testid="connect-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">Hubungkan Google Sheet</DialogTitle>
            </DialogHeader>
            <form onSubmit={connect} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nama Dashboard</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required
                  placeholder="mis. Tagihan RS Agustus" className="mt-1.5" data-testid="ds-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Link Google Sheet</Label>
                <Input value={url} onChange={(e) => setUrl(e.target.value)} required
                  placeholder="https://docs.google.com/spreadsheets/d/..." className="mt-1.5 text-xs"
                  data-testid="ds-url-input" />
                <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
                  <LinkSimple size={14} className="mt-0.5 shrink-0" />
                  Pastikan sheet dibagikan "Siapa saja yang memiliki link" atau File → Share → Publish to web.
                </p>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="rounded-xl w-full font-semibold" data-testid="ds-submit-button">
                  {saving ? "Menghubungkan & menganalisa..." : "Hubungkan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground animate-pulse">Memuat sumber data…</div>
      ) : sources.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center bg-card/50" data-testid="empty-sources">
          <Database size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">Belum ada sumber data terhubung</p>
          <p className="text-muted-foreground text-sm mt-1">Klik "Hubungkan Sheet" untuk memulai.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sources-grid">
          {sources.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="group rounded-2xl bg-card p-5 shadow-sm ring-1 ring-black/[0.03] hover:ring-primary/30 transition-shadow"
              data-testid={`source-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <Database size={22} className="text-primary" />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => refresh(s.id)} disabled={busyId === s.id}
                    className="p-1.5 text-muted-foreground hover:text-primary" title="Perbarui" data-testid={`refresh-${s.id}`}>
                    <ArrowClockwise size={16} className={busyId === s.id ? "animate-spin" : ""} />
                  </button>
                  <button onClick={() => remove(s.id)} disabled={busyId === s.id}
                    className="p-1.5 text-muted-foreground hover:text-destructive" title="Hapus" data-testid={`delete-${s.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              <h3 className="font-display font-semibold text-lg mt-3 truncate">{s.name}</h3>
              <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                <span><span className="text-foreground tabular font-semibold">{fmt(s.row_count)}</span> baris</span>
                <span><span className="text-foreground tabular font-semibold">{s.columns?.length || 0}</span> kolom</span>
              </div>
              <button onClick={() => navigate(`/dashboard/${s.id}`)}
                className="mt-5 flex items-center gap-2 text-sm text-primary hover:gap-3 transition-all font-medium"
                data-testid={`open-dashboard-${s.id}`}>
                Buka dashboard <ArrowRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
